import Reveal from "@/components/Reveal";
import { estimate, MACRS_5Y } from "@/lib/roi-model";

/**
 * Honest, deterministic solar arithmetic — the same shared 25-year cash-ownership
 * model that powers the homepage calculator. Illustrative only; never a quote.
 */
const RATE = 0.14; // $/kWh, illustrative blended Florida rate
const YIELD = 1450; // kWh produced per installed kW per year
const W_PER_SQFT = 7; // usable flat-roof packing density

function runModel(billMonthly, withTax) {
  return estimate({
    monthlySpend: billMonthly, buildingSqFt: 10000000, usableRoofFraction: 1,
    wattsPerUsableSqFt: 12, annualYield: YIELD, importRate: RATE,
    energyBillFraction: 0.65, selfUseFraction: 0.8, dollarsPerWatt: 2.65,
    annualOMFraction: 0.015, taxRate: 0.21,
    taxScheduleConfirmed: withTax, creditRate: withTax ? 0.3 : 0,
    creditUseYear: 1, depreciationFractions: withTax ? MACRS_5Y : Array(26).fill(0),
    financeMode: "cash", discountRate: 0.08,
  });
}

export function computeMath(billMonthly) {
  const plain = runModel(billMonthly, false);
  const tax = runModel(billMonthly, true);
  const monthlyKwh = Math.round(billMonthly / RATE);
  const annualKwh = monthlyKwh * 12;
  const kw = Math.round(plain.systemKW);
  const roofSqFt = Math.round((kw * 1000) / W_PER_SQFT);
  const watts = kw * 1000;
  const costLow = Math.round(watts * 2.3);
  const costHigh = Math.round(watts * 3.0);
  const costMid = Math.round((costLow + costHigh) / 2);
  const annualSavings = Math.round(plain.yearOneOperatingSavings * 0.79); // after-tax, 21% illustration
  const paybackPlain = plain.paybackYears == null ? null : Math.round(plain.paybackYears * 10) / 10;
  const paybackTaxScenario = tax.paybackYears == null ? null : Math.round(tax.paybackYears * 10) / 10;
  return {
    monthlyKwh, annualKwh, kw, roofSqFt, costLow, costHigh, costMid, annualSavings,
    paybackPlain, paybackTaxScenario,
  };
}

const money = (n) => `$${n.toLocaleString("en-US")}`;

function MathCard({ step, title, children }) {
  return (
    <article className="math-card" data-testid={`math-card-${step}`}>
      <span className="process-num">STEP {step}</span>
      <h3>{title}</h3>
      <div>{children}</div>
    </article>
  );
}

function Bar({ label, value, display, max, highlight }) {
  return (
    <div className="chart-row" style={highlight ? { background: "var(--mint)", borderRadius: 10 } : undefined}>
      <span className="chart-label">{label}</span>
      <span className="chart-track">
        <i style={{ width: `${Math.max(4, (value / max) * 100)}%` }} />
      </span>
      <span className="chart-value">{display}</span>
    </div>
  );
}

export default function SolarMath({ post }) {
  const m = computeMath(post.exampleBill);
  const billBands = [2500, 5000, post.exampleBill >= 8000 ? 10000 : 7500, 25000];
  const bands = billBands.map((b) => ({ bill: b, ...computeMath(b) }));
  const maxKw = Math.max(...bands.map((b) => b.kw));

  return (
    <>
      <section data-testid="blog-math">
        <span className="eyebrow">DO THE MATH YOURSELF</span>
        <h2>The {post.city} math, step by step</h2>
        <p className="muted" style={{ fontSize: 13, maxWidth: 720 }}>
          The same four moves behind every serious solar proposal, worked with a {money(post.exampleBill)}/month example for {post.city}.
          Illustrative only — not a quote, and tax scenarios need your CPA.
        </p>
        <div className="math-grid">
          <MathCard step={1} title="Bill → monthly kWh">
            {post.city} businesses pay for energy, demand and riders. Using an illustrative blended rate of{" "}
            <b>${(RATE * 100).toFixed(0)}¢ per kWh</b>: {money(post.exampleBill)} ÷ ${(RATE * 100).toFixed(0)}¢ ≈{" "}
            <b>{m.monthlyKwh.toLocaleString("en-US")} kWh per month</b> ({(m.annualKwh / 1000).toFixed(0)} MWh a year).
          </MathCard>
          <MathCard step={2} title="Usage → system size">
            Florida systems produce roughly <b>{YIELD.toLocaleString("en-US")} kWh per installed kW each year</b>.{" "}
            {m.annualKwh.toLocaleString("en-US")} kWh ÷ {YIELD.toLocaleString("en-US")} ≈{" "}
            <b>a {m.kw.toLocaleString("en-US")} kW system</b> to offset the usage this bill implies.
          </MathCard>
          <MathCard step={3} title="Size → roof check">
            Flat commercial roofs pack about <b>{W_PER_SQFT} watts per square foot</b> after walkways and obstructions.{" "}
            {m.kw.toLocaleString("en-US")} kW needs roughly <b>{m.roofSqFt.toLocaleString("en-US")} sq ft of usable roof</b> —
            which is why the roof face, not the lot, sets the ceiling.
          </MathCard>
          <MathCard step={4} title="Size → cost → payback">
            At Florida's typical <b>$2.30–$3.00 per watt installed</b>, a {m.kw.toLocaleString("en-US")} kW system runs{" "}
            <b>{money(m.costLow)}–{money(m.costHigh)}</b>. Dividing net cost by after-tax savings (~79¢ of each dollar, illustration):
            <b> ~{m.paybackPlain} years</b> as a straight purchase, or <b>~{m.paybackTaxScenario} years</b> in a CPA-confirmed
            credit-plus-depreciation scenario.
          </MathCard>
        </div>
      </section>

      <section style={{ marginTop: 34 }}>
        <span className="eyebrow">WHAT BILLS IMPLY</span>
        <h2>System size by monthly bill</h2>
        <div className="chart" data-testid="blog-chart-bills">
          {bands.map((b) => (
            <Bar key={b.bill} label={`${money(b.bill)}/mo`} value={b.kw} display={`${b.kw.toLocaleString("en-US")} kW`} max={maxKw} highlight={b.bill === post.exampleBill} />
          ))}
        </div>
        <p className="micro">Illustrative arithmetic at ${RATE.toFixed(2)}/kWh and {YIELD.toLocaleString("en-US")} kWh/kW·yr — real sizing starts with your bills and roof.</p>
      </section>

      <section style={{ marginTop: 30 }}>
        <span className="eyebrow">INSTALLED COST RANGE</span>
        <h2>What a {post.city} project might price</h2>
        <div className="chart" data-testid="blog-chart-cost">
          <div className="chart-row">
            <span className="chart-label">Panels only</span>
            <span className="chart-track">
              <i className="range" style={{ width: "100%" }} />
            </span>
            <span className="chart-value">{money(m.costLow)}–{money(m.costHigh)}</span>
          </div>
          <div className="chart-row">
            <span className="chart-label">+ battery</span>
            <span className="chart-track">
              <i className="range" style={{ width: "100%", backgroundImage: "repeating-linear-gradient(45deg,var(--teal),var(--teal) 6px,#0d5a52 6px,#0d5a52 12px)" }} />
            </span>
            <span className="chart-value">quoted per backup loads</span>
          </div>
        </div>
        <p className="micro">Typical Florida installed range of $2.30–$3.00/W for the {m.kw.toLocaleString("en-US")} kW example; battery cost depends on which loads you back up, so it is always quoted separately.</p>
      </section>

      <section style={{ marginTop: 30 }}>
        <span className="eyebrow">PAYBACK SCENARIOS</span>
        <h2>How incentives move the timeline</h2>
        <div className="chart" data-testid="blog-chart-payback">
          <div className="chart-row">
            <span className="chart-label">Straight purchase</span>
            <span className="chart-track">
              <i style={{ width: `${Math.min(100, (m.paybackPlain / 25) * 100)}%` }} />
            </span>
            <span className="chart-value">~{m.paybackPlain} yrs</span>
          </div>
          <div className="chart-row">
            <span className="chart-label">With CPA-confirmed tax scenarios</span>
            <span className="chart-track">
              <i style={{ width: `${Math.min(100, (m.paybackTaxScenario / 25) * 100)}%`, background: "var(--teal)" }} />
            </span>
            <span className="chart-value">~{m.paybackTaxScenario} yrs</span>
          </div>
        </div>
        <p className="micro">Scenario arithmetic only — the federal credit and depreciation apply only when your CPA confirms eligibility, basis and timing. This site never assumes you qualify.</p>
      </section>
    </>
  );
}
